import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Draggable from "react-draggable";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import asset from "json/assets";
import useNotiStack from "hooks/useNotistack";
import {
  useCourseGPSMappingData,
  useTransformedHoleData,
} from "hooks/CourseMapDetails";
import { useProfileUsername } from "hooks/ProfileDetails";
import { decryptSecretKey, getModalProperties } from "services/utils";
import ModalWrapper from "layout/ModalWrapper/ModalWrapper";
import NoificationModal from "pages/ModalPage/NoificationModal";
import {
  course_map_gps_mapping_details,
  course_map_gps_vector_details,
  course_mapping_layout_update,
  course_mapping_note_create,
  course_mapping_note_list,
  course_scorecard_details,
} from "redux/CourseMapSlice";
import popOverStyles from "styles/component/CustomCoursePopOver.module.scss";
import styles from "styles/component/ToolsMenu.module.scss";
import { evaluateFlipPreconditions } from "./duplicateBackNine";

const FlipOptionLabel = ({ label, reason }) => {
  if (!reason) {
    return <span>{label}</span>;
  }

  return (
    <Box className={styles.checkbox_label}>
      <span>{label}</span>
      <Tooltip title={reason} arrow placement="top">
        <IconButton className={styles.checkbox_info_btn} size="small">
          <InfoOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const FlipNinesModal = ({ onClose, onSuccess, layoutHoles }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id_courseFromQuery = searchParams.get("course");
  const secret_Key = decryptSecretKey();
  const username = useProfileUsername();
  const holeWiseData = useTransformedHoleData();
  const courseGPSMapping = useCourseGPSMappingData();
  const { toastSuccess, toastError, toastWarning } = useNotiStack();
  const [scorecard, setScorecard] = useState(false);
  const [simpleMap, setSimpleMap] = useState(false);
  const [vectorMap, setVectorMap] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const modalProperties = getModalProperties();

  const user = { secretKey: secret_Key, userName: username };
  const flipAvailability = evaluateFlipPreconditions({
    layoutHoles,
    holeWiseData,
    gpsList: courseGPSMapping?.GPSList,
  });
  const canSubmit = (scorecard || simpleMap || vectorMap) && !submitting;

  const selectedDataTypes = [
    scorecard && "Scorecard",
    simpleMap && "Simple Map",
    vectorMap && "Vector Map",
  ]
    .filter(Boolean)
    .join(", ");

  const refreshCourseData = () => {
    const body = { id_course: id_courseFromQuery };
    dispatch(course_map_gps_mapping_details({ body, user }));
    dispatch(course_map_gps_vector_details({ body, user }));
    dispatch(course_scorecard_details({ body, user }));
  };

  const createFlipNote = () => {
    dispatch(
      course_mapping_note_create({
        body: {
          note: `Flipped the 9's -> ${selectedDataTypes}`,
          id_course: Number(id_courseFromQuery),
        },
        user,
      })
    )?.then((res) => {
      if (Number(res?.payload?.data?.Status) === 1) {
        dispatch(
          course_mapping_note_list({
            body: { id_course: id_courseFromQuery },
            user,
          })
        );
      } else {
        toastWarning(
          "The 9's were flipped, but the mapping note could not be created."
        );
      }
    });
  };

  const handleSubmit = () => {
    setConfirmOpen(false);
    setSubmitting(true);
    dispatch(
      course_mapping_layout_update({
        body: {
          id_course: Number(id_courseFromQuery),
          flipNines: 1,
          updateScorecard: scorecard ? 1 : 0,
          updateGPS: simpleMap ? 1 : 0,
          updateVector: vectorMap ? 1 : 0,
        },
        user,
      })
    ).then((res) => {
      const data = res?.payload?.data;
      if (Number(data?.Status) === 1) {
        toastSuccess("Flipped the 9's");
        createFlipNote();
        setTimeout(refreshCourseData, 500);
        onSuccess();
      } else {
        setSubmitting(false);
        toastError(`Flip 9's failed. ${data?.ErrorMessage ?? "Unknown error"}`);
      }
    });
  };

  return (
    <>
    <Draggable
      bounds={"#DraggableContainer"}
      defaultPosition={{ x: 0, y: 0 }}
      cancel=".MuiSlider-root, button, input"
    >
      <Box
        className={`${popOverStyles.poover_content} ${popOverStyles.default_poover_content} ${styles.flip_panel} custom_popover_content`}
      >
        <Box className={popOverStyles.poover_head}>
          <Typography variant="h5">Flip 9's</Typography>
          <List>
            <ListItem>
              <Tooltip title="Close" arrow placement="top">
                <Button onClick={onClose}>
                  <img src={asset.close} alt="" />
                </Button>
              </Tooltip>
            </ListItem>
          </List>
        </Box>
        <Box className={`${popOverStyles.popover_body} ${styles.panel_body}`}>
          <Typography className={styles.data_types_label}>
            Data Types:
          </Typography>
          <Box className={styles.checkbox_list}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={scorecard}
                  onChange={(e) => setScorecard(e.target.checked)}
                />
              }
              label="Scorecard"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={simpleMap}
                  disabled={!flipAvailability.simpleMap.allowed}
                  onChange={(e) => setSimpleMap(e.target.checked)}
                />
              }
              label={
                <FlipOptionLabel
                  label="Simple Map"
                  reason={flipAvailability.simpleMap.reason}
                />
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={vectorMap}
                  disabled={!flipAvailability.vectorMap.allowed}
                  onChange={(e) => setVectorMap(e.target.checked)}
                />
              }
              label={
                <FlipOptionLabel
                  label="Vector Map"
                  reason={flipAvailability.vectorMap.reason}
                />
              }
            />
          </Box>
          {(!flipAvailability.simpleMap.allowed ||
            !flipAvailability.vectorMap.allowed) && (
            <Typography className={styles.flip_help_text}>
              Empty holes cannot be flipped. Fill holes 10-18 or use Duplicate
              to Back 9 first.
            </Typography>
          )}
          <Button
            className={styles.submit_btn}
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
          >
            SUBMIT
          </Button>
        </Box>
      </Box>
    </Draggable>
    <ModalWrapper
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      handleClose={() => setConfirmOpen(false)}
    >
      <NoificationModal
        label="Flip 9's"
        backgroundColor={modalProperties.info.backgroundColor}
        image={modalProperties.warning.icon}
        title={`<span style='font-size:14px'>Are you sure you want to flip the 9's<br/> for course #${id_courseFromQuery}?<br/>Data to update: ${selectedDataTypes}</span>`}
        rejCallBack={() => setConfirmOpen(false)}
        affrCallBack={handleSubmit}
        affrBtnTxt={"Continue"}
        rejBtnTxt={"Cancel"}
      />
    </ModalWrapper>
    </>
  );
};

export default FlipNinesModal;
